import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F9F8F6' }}>
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#2D3748' }}>
            Enlace Inválido
          </h1>
          <p className="text-gray-600 mb-6">
            Este enlace de recuperación no es válido o ha expirado.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full" style={{ backgroundColor: '#5A7D6C' }}>
              Solicitar Nuevo Enlace
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Contraseña restablecida exitosamente');
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al restablecer contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F9F8F6' }}>
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#2D3748' }}>
            ¡Contraseña Restablecida!
          </h1>
          <p className="text-gray-600 mb-6">
            Tu contraseña ha sido actualizada exitosamente.
            <br />
            Serás redirigido al login...
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="w-full"
            style={{ backgroundColor: '#5A7D6C' }}
          >
            Ir al Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F9F8F6' }}>
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8" style={{ color: '#5A7D6C' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Restablecer Contraseña
          </h1>
          <p className="text-sm text-gray-600">
            Ingrese su nueva contraseña
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">Nueva Contraseña</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="confirm-password">Confirmar Contraseña</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repita su contraseña"
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            style={{ backgroundColor: '#5A7D6C' }}
          >
            {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/auth" 
            className="text-sm hover:underline"
            style={{ color: '#5A7D6C' }}
          >
            Volver al Login
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
